import { getErrorMessage } from '../../utils/errorMessage';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Building2,
  Check,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  Download,
  FileCheck2,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import {
  createInvoice,
  getEnterpriseCertification,
  getInvoiceEligibleOrders,
  getInvoices,
  getPersonalCertification,
} from './corporateFinanceApi';
import EnterpriseCertificationPanel, { normalizeEnterpriseCertificationStatus } from './EnterpriseCertificationPanel';
import PersonalCertificationPanel, { normalizePersonalCertificationStatus } from './PersonalCertificationPanel';
import './CorporateFinancePanel.css';

const MAX_INVOICE_ORDERS = 20;
const EMPTY_INVOICE = {
  title_type: 'company',
  invoice_email: '',
};

const dateText = value => value ? new Date(value).toLocaleString('zh-CN') : '—';
const moneyText = value => `¥${Number(value || 0).toFixed(2)}`;
const amountCents = value => {
  const match = String(value ?? '').match(/^(\d+)(?:\.(\d{1,2}))?$/);
  if (!match) return null;
  const cents = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : null;
};
const basisLabel = basis => basis === 'consumption' ? '按消费' : '按订单';
const createInvoiceKey = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map(value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
const statusTone = status => Number(status) === 1 ? 'success' : (Number(status) === 2 ? 'quiet' : 'pending');

export default function CorporateFinancePanel({ user, preview = false, english = false }) {
  const [scope, setScope] = useState('personal');
  const scopeRef = useRef(scope);
  const scopeEpoch = useRef(0);
  const [invoiceBasis, setInvoiceBasis] = useState('order');
  const basisRef = useRef(invoiceBasis);
  const ordersRequest = useRef(0);
  const submittingRef = useRef(false);
  const submissionIdentity = useRef(null);
  const [coverage, setCoverage] = useState(null);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [invoice, setInvoice] = useState({ ...EMPTY_INVOICE, invoice_email: user?.email || '' });
  const [query, setQuery] = useState('');
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [error, setError] = useState('');
  const [ordersError, setOrdersError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [certificationEditing, setCertificationEditing] = useState(false);
  const [personalCertificationEditing, setPersonalCertificationEditing] = useState(false);
  const [certificationState, setCertificationState] = useState({
    certification: null,
    permissions: { can_submit: false, can_invoice: false },
    loading: true,
    error: '',
  });
  const [personalCertificationState, setPersonalCertificationState] = useState({
    certification: null,
    permissions: { can_submit: false, can_invoice: false },
    reviewNotice: '',
    loading: true,
    error: '',
  });

  const loadInvoices = async currentScope => {
    const epoch = scopeEpoch.current;
    setLoading(true);
    setError('');
    try {
      if (preview) {
        if (scopeRef.current === currentScope && epoch === scopeEpoch.current) setInvoices([]);
        return;
      }
      const data = await getInvoices({ per_page: 30, scope: currentScope });
      if (scopeRef.current === currentScope && epoch === scopeEpoch.current) setInvoices(data.list || []);
    } catch (requestError) {
      if (scopeRef.current === currentScope && epoch === scopeEpoch.current) setError(getErrorMessage(requestError, '开票记录加载失败'));
    } finally {
      if (scopeRef.current === currentScope && epoch === scopeEpoch.current) setLoading(false);
    }
  };

  const loadCertification = async currentScope => {
    const epoch = scopeEpoch.current;
    setCertificationState(current => ({ ...current, loading: true, error: '' }));
    try {
      if (preview) {
        if (scopeRef.current === currentScope && epoch === scopeEpoch.current) {
          setCertificationState({
            certification: null,
            permissions: { can_submit: false, can_invoice: false },
            loading: false,
            error: '',
          });
        }
        return;
      }
      const data = await getEnterpriseCertification(currentScope);
      if (scopeRef.current === currentScope && epoch === scopeEpoch.current) {
        setCertificationState({
          certification: data.certification || null,
          permissions: data.permissions || { can_submit: false, can_invoice: false },
          loading: false,
          error: '',
        });
      }
    } catch (requestError) {
      if (scopeRef.current === currentScope && epoch === scopeEpoch.current) {
        setCertificationState({
          certification: null,
          permissions: { can_submit: false, can_invoice: false },
          loading: false,
          error: getErrorMessage(requestError, '企业认证状态加载失败'),
        });
      }
    }
  };

  const loadPersonalCertification = async currentScope => {
    const epoch = scopeEpoch.current;
    setPersonalCertificationState(current => ({ ...current, loading: true, error: '' }));
    try {
      if (preview || currentScope !== 'personal') {
        if (scopeRef.current === currentScope && epoch === scopeEpoch.current) {
          setPersonalCertificationState({
            certification: null,
            permissions: { can_submit: false, can_invoice: false },
            reviewNotice: '',
            loading: false,
            error: '',
          });
        }
        return;
      }
      const data = await getPersonalCertification();
      if (scopeRef.current === currentScope && epoch === scopeEpoch.current) {
        setPersonalCertificationState({
          certification: data.certification || null,
          permissions: data.permissions || { can_submit: false, can_invoice: false },
          reviewNotice: data.review_notice || '',
          loading: false,
          error: '',
        });
      }
    } catch (requestError) {
      if (scopeRef.current === currentScope && epoch === scopeEpoch.current) {
        setPersonalCertificationState({
          certification: null,
          permissions: { can_submit: false, can_invoice: false },
          reviewNotice: '',
          loading: false,
          error: getErrorMessage(requestError, '个人认证状态加载失败'),
        });
      }
    }
  };

  const loadOrders = async (currentScope, currentBasis = basisRef.current) => {
    const requestId = ++ordersRequest.current;
    const isCurrent = () => requestId === ordersRequest.current && scopeRef.current === currentScope && basisRef.current === currentBasis;
    setOrdersLoading(true);
    setOrdersError('');
    submissionIdentity.current = null;
    setSelectedOrders([]);
    setOrders([]);
    setCoverage(null);
    try {
      if (preview) {
        return;
      }
      const data = await getInvoiceEligibleOrders(currentScope, currentBasis);
      if (isCurrent()) {
        if (currentBasis === 'consumption' && data.invoice_basis !== 'consumption') {
          throw new Error('开票服务尚未支持按消费，请联系管理员更新服务；您仍可按订单开票');
        }
        setOrders((data.list || []).filter(order => amountCents(order.amount) > 0));
        setCoverage(data.coverage || null);
      }
    } catch (requestError) {
      if (isCurrent()) {
        setOrders([]);
        setOrdersError(getErrorMessage(requestError, '可开票订单加载失败'));
      }
    } finally {
      if (isCurrent()) setOrdersLoading(false);
    }
  };

  useEffect(() => {
    setInvoice(current => ({ ...current, invoice_email: current.invoice_email || user?.email || '' }));
  }, [user?.email]);
  useEffect(() => {
    scopeRef.current = scope;
    setSelectedOrders([]);
    setQuery('');
    setNotice('');
    setError('');
    setCertificationEditing(false);
    setPersonalCertificationEditing(false);
    setInvoice(current => ({ ...current, title_type: scope === 'team' ? 'company' : current.title_type }));
    loadInvoices(scope);
    loadCertification(scope);
    loadPersonalCertification(scope);
  }, [preview, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadOrders(scope, invoiceBasis);
    return () => { ordersRequest.current += 1; };
  }, [preview, scope, invoiceBasis]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredOrders = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return orders;
    return orders.filter(order => [order.order_no, order.subject, order.invoice_issuer]
      .some(value => String(value || '').toLowerCase().includes(keyword)));
  }, [orders, query]);

  const selectedRows = useMemo(
    () => orders.filter(order => selectedOrders.includes(order.id)),
    [orders, selectedOrders],
  );
  const invoiceCents = useMemo(
    () => selectedRows.reduce((total, order) => total + amountCents(order.amount), 0),
    [selectedRows],
  );
  const invoiceAmount = Number.isSafeInteger(invoiceCents) ? `${Math.floor(invoiceCents / 100)}.${String(invoiceCents % 100).padStart(2, '0')}` : '0.00';
  const allFilteredSelected = filteredOrders.length > 0
    && filteredOrders.every(order => selectedOrders.includes(order.id));
  const invoiceIssuer = selectedRows[0]?.invoice_issuer || orders[0]?.invoice_issuer || '以实际电子发票为准';
  const invoiceTaxRate = selectedRows[0]?.invoice_tax_rate || orders[0]?.invoice_tax_rate || '—';
  const certification = certificationState.certification;
  const certificationStatus = normalizeEnterpriseCertificationStatus(certification);
  const companyInvoiceReady = !certificationState.loading
    && !certificationEditing
    && Boolean(certificationState.permissions?.can_invoice)
    && certificationStatus === 'approved'
    && Boolean(certification?.can_invoice)
    && Boolean(certification?.id);
  const personalCertification = personalCertificationState.certification;
  const personalCertificationStatus = normalizePersonalCertificationStatus(personalCertification);
  const personalInvoiceReady = scope === 'personal'
    && invoice.title_type === 'personal'
    && !personalCertificationState.loading
    && !personalCertificationState.error
    && !personalCertificationEditing
    && Boolean(personalCertificationState.permissions?.can_invoice)
    && personalCertificationStatus === 'approved'
    && Boolean(personalCertification?.can_invoice)
    && Boolean(personalCertification?.id);
  const currentInvoiceReady = personalInvoiceReady || (invoice.title_type === 'company' && companyInvoiceReady);
  const canSelectOrders = invoice.title_type === 'personal' ? personalInvoiceReady : companyInvoiceReady;
  const personalBlockedReason = personalCertificationEditing
    ? '个人实名资料正在更新，个人开票已暂停；取消修改可恢复原认证身份'
    : personalCertificationState.error
    ? '个人认证状态暂时无法核验，请重新加载后再申请个人发票'
    : personalCertificationState.loading
      ? '正在核验个人认证状态，请稍候'
      : personalCertificationStatus === 'pending'
        ? '个人认证正在审核，审核通过后才能申请个人发票'
        : ['rejected', 'revoked'].includes(personalCertificationStatus)
          ? `个人认证未通过${personalCertification?.review_remark ? `：${personalCertification.review_remark}` : '，请根据审核说明修正资料后重新提交'}`
          : personalCertificationStatus === 'approved'
            ? '个人认证已通过，但当前个人开票资格暂不可用，请联系客服处理'
            : '请先填写姓名、身份证号并上传身份证正反面照片；审核通过后才能申请个人发票';
  const blockedReason = invoice.title_type === 'personal' && scope === 'team'
    ? '团队订单仅支持使用已认证企业主体开票'
    : invoice.title_type === 'personal' && !personalInvoiceReady
      ? personalBlockedReason
    : invoice.title_type === 'company' && certificationEditing
      ? '企业资料正在修改，企业开票已暂停；取消修改可恢复原认证主体'
      : invoice.title_type === 'company' && !companyInvoiceReady
      ? (scope === 'team' && !certificationState.permissions?.can_submit
        ? '当前成员没有团队企业开票权限，请联系团队所有者或管理员'
        : `企业认证审核通过后才能申请企业发票${scope === 'personal' ? '；也可切换为个人抬头并完成个人认证' : ''}`)
      : '';

  const switchScope = nextScope => {
    if (submittingRef.current || nextScope === scopeRef.current) return;
    scopeEpoch.current += 1;
    submissionIdentity.current = null;
    ordersRequest.current += 1;
    scopeRef.current = nextScope;
    setOrders([]);
    setSelectedOrders([]);
    setScope(nextScope);
  };

  const switchBasis = nextBasis => {
    if (submittingRef.current || nextBasis === basisRef.current) return;
    basisRef.current = nextBasis;
    submissionIdentity.current = null;
    ordersRequest.current += 1;
    setOrders([]);
    setSelectedOrders([]);
    setQuery('');
    setNotice('');
    setError('');
    setInvoiceBasis(nextBasis);
  };

  const switchTitleType = titleType => {
    if (submittingRef.current) return;
    submissionIdentity.current = null;
    setInvoice(current => ({ ...current, title_type: titleType }));
    setPersonalCertificationEditing(false);
    setError('');
    setNotice('');
  };

  const toggleOrder = orderId => {
    if (submittingRef.current) return;
    submissionIdentity.current = null;
    setError('');
    setSelectedOrders(current => {
      if (current.includes(orderId)) return current.filter(id => id !== orderId);
      if (current.length >= MAX_INVOICE_ORDERS) {
        setError(getErrorMessage(`一次最多选择 ${MAX_INVOICE_ORDERS} 笔订单，请分批开票`));
        return current;
      }
      return [...current, orderId];
    });
  };

  const toggleAllFiltered = () => {
    if (submittingRef.current) return;
    submissionIdentity.current = null;
    setError('');
    if (allFilteredSelected) {
      const visibleIds = new Set(filteredOrders.map(order => order.id));
      setSelectedOrders(current => current.filter(id => !visibleIds.has(id)));
      return;
    }
    setSelectedOrders(current => {
      const next = [...current];
      filteredOrders.forEach(order => {
        if (!next.includes(order.id) && next.length < MAX_INVOICE_ORDERS) next.push(order.id);
      });
      if (next.length < current.length + filteredOrders.filter(order => !current.includes(order.id)).length) {
        setNotice(`已按单次上限选择前 ${MAX_INVOICE_ORDERS} 笔订单`);
      }
      return next;
    });
  };

  const submitInvoice = async event => {
    event.preventDefault();
    if (preview || submittingRef.current || ordersLoading) return;
    setError('');
    setNotice('');
    if (!selectedRows.length || invoiceAmount === '0.00') {
      setError('请至少选择一笔有可开票额度的订单');
      return;
    }
    if (!invoice.invoice_email.trim()) {
      setError('请填写用于接收电子发票的邮箱');
      return;
    }
    if (invoice.title_type === 'company' && !companyInvoiceReady) {
      setError('企业认证审核通过后才能申请企业发票');
      return;
    }
    if (invoice.title_type === 'personal' && scope === 'team') {
      setError('团队订单仅支持企业发票');
      return;
    }
    if (invoice.title_type === 'personal' && !personalInvoiceReady) {
      setError(getErrorMessage(personalBlockedReason));
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const payload = {
        scope,
        invoice_basis: invoiceBasis,
        expected_amount: invoiceAmount,
        order_ids: selectedRows.map(order => order.id),
        title_type: invoice.title_type,
        invoice_email: invoice.invoice_email.trim(),
        ...(invoice.title_type === 'company'
          ? { enterprise_certification_id: certification.id }
          : { personal_certification_id: personalCertification.id }),
      };
      const fingerprint = JSON.stringify(payload);
      if (submissionIdentity.current?.fingerprint !== fingerprint) {
        submissionIdentity.current = { fingerprint, key: createInvoiceKey() };
      }
      payload.idempotency_key = submissionIdentity.current.key;
      const result = await createInvoice(payload);
      submissionIdentity.current = null;
      setNotice(result.result_pending
        ? '申请已受理，服务商结果待核验；为避免重复开票，请勿再次提交相同订单'
        : '电子发票已开具，可在下方开票记录中查看');
      setInvoice({ ...EMPTY_INVOICE, invoice_email: user?.email || '' });
      setSelectedOrders([]);
      await Promise.all([loadInvoices(scope), loadOrders(scope)]);
    } catch (submitError) {
      setError(getErrorMessage(submitError, '开票申请提交失败'));
      if (submitError.code === 'INVOICE_AMOUNT_CHANGED') await loadOrders(scope, invoiceBasis);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) {
    return <section className="corporate-finance-state" aria-live="polite"><Loader2 className="canvas-vnext-spin" size={18} /><strong>正在加载开票服务</strong></section>;
  }

  return <section className="corporate-finance-panel">
    <header className="account-page-heading">
      <div>
        <span className="account-eyebrow">INVOICES</span>
        <h1>{english ? 'Online Invoices' : '在线开票'}</h1>
        <p>{english ? 'Request an e-invoice for paid orders or eligible consumption.' : '按已支付订单或已核定的消费金额，申请数电普通发票。'}</p>
      </div>
      <span className="account-status account-status--neutral"><FileCheck2 size={13} />数电普通发票</span>
    </header>

    <div className="corporate-finance-context">
      <div className="corporate-finance-scope" role="group" aria-label="业务账户">
        <span>业务账户</span>
        <button type="button" disabled={submitting} className={scope === 'personal' ? 'is-active' : ''} aria-pressed={scope === 'personal'} onClick={() => switchScope('personal')}>个人账户</button>
        <button type="button" disabled={submitting} className={scope === 'team' ? 'is-active' : ''} aria-pressed={scope === 'team'} onClick={() => switchScope('team')}>当前团队</button>
      </div>
      {scope === 'personal' && <div className="corporate-finance-scope" role="group" aria-label="开票抬头类型">
        <span>开票抬头</span>
        <button type="button" disabled={submitting} className={invoice.title_type === 'company' ? 'is-active' : ''} aria-pressed={invoice.title_type === 'company'} onClick={() => switchTitleType('company')}>企业发票</button>
        <button type="button" disabled={submitting} className={invoice.title_type === 'personal' ? 'is-active' : ''} aria-pressed={invoice.title_type === 'personal'} onClick={() => switchTitleType('personal')}>个人发票</button>
      </div>}
    </div>
    {notice && <p className="corporate-finance-notice" role="status"><Check size={13} />{notice}</p>}
    {error && <p className="corporate-finance-error" role="alert">{error}<button type="button" onClick={() => setError('')}>关闭</button></p>}

    {scope === 'personal' && invoice.title_type === 'personal' ? (
      <PersonalCertificationPanel
        user={user}
        certification={personalCertificationState.certification}
        permissions={personalCertificationState.permissions}
        reviewNotice={personalCertificationState.reviewNotice}
        loading={personalCertificationState.loading}
        error={personalCertificationState.error}
        onRetry={() => loadPersonalCertification(scope)}
        onChanged={() => loadPersonalCertification(scope)}
        onSubmitted={message => setNotice(message)}
        onEditingChange={setPersonalCertificationEditing}
        preview={preview}
      />
    ) : (
      <EnterpriseCertificationPanel
        scope={scope}
        user={user}
        certification={certificationState.certification}
        permissions={certificationState.permissions}
        loading={certificationState.loading}
        error={certificationState.error}
        onRetry={() => loadCertification(scope)}
        onChanged={() => loadCertification(scope)}
        onEditingChange={setCertificationEditing}
        preview={preview}
      />
    )}

    <section className="invoice-order-picker" aria-labelledby="invoice-order-title">
      <header>
        <div className="invoice-order-title"><span className="enterprise-certification-step">2</span><div><h2 id="invoice-order-title">选择开票内容</h2><p>仅展示当前仍有可开票额度的订单；一次最多选择 {MAX_INVOICE_ORDERS} 笔。</p></div></div>
        <div><span>已选 {selectedRows.length} 笔</span><strong>{moneyText(invoiceAmount)}</strong></div>
      </header>
      <div className="invoice-basis-section">
        <div className="corporate-finance-scope" role="group" aria-label="开票方式">
          <button type="button" aria-pressed={invoiceBasis === 'order'} className={invoiceBasis === 'order' ? 'is-active' : ''} disabled={submitting} onClick={() => switchBasis('order')}>按订单</button>
          <button type="button" aria-pressed={invoiceBasis === 'consumption'} className={invoiceBasis === 'consumption' ? 'is-active' : ''} disabled={submitting} onClick={() => switchBasis('consumption')}>按消费</button>
        </div>
        <p>{invoiceBasis === 'consumption' ? '按已核定的消费金额开票，消费多少开多少；按对应的付费订单汇总。' : '按订单剩余可开票金额申请，包含尚未消费的部分。'}两种方式共用额度，已开票及申请中的金额不会重复计入。</p>
        {invoiceBasis === 'consumption' && <p className="invoice-coverage-note"><CircleAlert size={13} /><span>{coverage?.message || '仅计入成功且可追溯到付费来源的消费。历史来源不完整的记录暂不计入，其他符合条件的金额仍可按订单开票。'}</span></p>}
      </div>
      <div className="invoice-order-toolbar">
        <label className="invoice-select-all">
          <input type="checkbox" checked={allFilteredSelected} onChange={toggleAllFiltered} disabled={submitting || ordersLoading || !filteredOrders.length || !canSelectOrders} />
          <span>全选当前结果</span>
        </label>
        <label className="invoice-order-search">
          <Search size={13} />
          <input aria-label="搜索可开票订单" value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索订单号、商品或开票主体" />
        </label>
        <span>共 {orders.length} 笔可开票</span>
      </div>
      <div className="invoice-order-table" role="table" aria-label="可开票订单">
        <div className="invoice-order-table__head" role="row">
          <span role="columnheader">选择</span><span role="columnheader">订单 / 账单</span><span role="columnheader">开票主体</span><span role="columnheader">开票内容</span><span role="columnheader">本次可开票</span><span role="columnheader">支付时间</span>
        </div>
        {ordersLoading ? <div className="corporate-finance-empty"><Loader2 className="canvas-vnext-spin" size={16} />正在加载可开票订单</div>
          : ordersError ? <div className="corporate-finance-empty is-error"><span>{ordersError}</span><button type="button" onClick={() => loadOrders(scope)}><RefreshCw size={12} />重新加载</button></div>
            : filteredOrders.length ? filteredOrders.map(order => <label className="invoice-order-table__row" role="row" key={order.id}>
              <span role="cell"><input type="checkbox" aria-label={`选择 ${order.order_no}`} checked={selectedOrders.includes(order.id)} onChange={() => toggleOrder(order.id)} disabled={submitting || !canSelectOrders} /></span>
              <span role="cell"><strong>{order.order_no}</strong><small>{order.subject}</small>{order.order_amount != null && <small>订单实付 {moneyText(order.order_amount)} · 已开／申请中 {moneyText(order.invoiced_amount)}</small>}</span>
              <span role="cell"><strong>{order.invoice_issuer || '以电子发票为准'}</strong><small>税率 {order.invoice_tax_rate || '—'}</small></span>
              <span role="cell">{order.invoice_content || order.subject}{invoiceBasis === 'consumption' && <small>{order.consumed_amount == null ? '消费金额暂无法核定' : `已核定消费累计 ${moneyText(order.consumed_amount)}`}{order.coverage_status === 'partial' ? '（部分记录）' : ''}</small>}{invoiceBasis === 'consumption' && order.exclusion_reason && <small>{order.exclusion_reason}</small>}</span>
              <b role="cell">{moneyText(order.amount)}</b>
              <time role="cell"><span className="invoice-paid-label">支付时间 </span>{dateText(order.paid_at)}</time>
            </label>) : <div className="corporate-finance-empty">{orders.length ? '没有匹配的订单' : invoiceBasis === 'consumption' ? (coverage?.status === 'unavailable' ? '暂无法核定可开票消费，请切换按订单查看' : '暂无已核定且可开票的消费') : '暂无可开票订单'}</div>}
      </div>
    </section>

    <form className="corporate-finance-form invoice-confirmation" aria-label="确认开票信息" onSubmit={submitInvoice}>
      <div className="corporate-finance-form__heading">
        <div><h2>确认开票信息</h2><p>{invoiceBasis === 'consumption' ? '所选订单中已核定的可开票消费金额将合并为 1 张电子发票。' : '所选订单的剩余可开票金额将合并为 1 张电子发票。'}明细保留本次开票金额。</p></div>
        <strong>{moneyText(invoiceAmount)}</strong>
      </div>
      <div className="invoice-issuer-strip"><span><ShieldCheck size={14} />开票主体</span><strong>{invoiceIssuer}</strong><small>数电普通发票 · 税率 {invoiceTaxRate}</small></div>

      {selectedRows.length > 0 && <details className="invoice-selected-details">
        <summary>已选开票明细（{selectedRows.length} 笔 · {basisLabel(invoiceBasis)}）</summary>
        <div>{selectedRows.map(order => <p key={order.id}><span>{order.order_no} · {order.subject}</span><strong>{moneyText(order.amount)}</strong></p>)}</div>
      </details>}

      <div className="corporate-finance-fields two">
        <label><span>抬头类型</span><select value={invoice.title_type} onChange={event => switchTitleType(event.target.value)} disabled={submitting || scope === 'team'}><option value="company">企业发票</option><option value="personal" disabled={scope === 'team'}>个人发票</option></select>{scope === 'team' && <small>团队订单仅支持企业发票</small>}</label>
        <label><span>接收邮箱</span><input type="email" disabled={submitting} value={invoice.invoice_email} maxLength={100} onChange={event => { submissionIdentity.current = null; setInvoice(current => ({ ...current, invoice_email: event.target.value })); }} placeholder="用于接收电子发票" /></label>
      </div>
      {invoice.title_type === 'company' ? (
        companyInvoiceReady ? <div className="invoice-certified-identity">
          <span><Building2 size={14} />认证企业</span>
          <strong>{certification.company_name}</strong>
          <code>{certification.unified_social_credit_code}</code>
          <small><ShieldCheck size={12} />使用已认证的企业信息开票</small>
        </div> : <div className="invoice-certification-gate" role="status"><CircleAlert size={15} /><span>{blockedReason}</span></div>
      ) : personalInvoiceReady ? <div className="invoice-certified-identity">
        <span><UserRound size={14} />认证个人</span>
        <strong>{personalCertification.real_name}</strong>
        <code>{personalCertification.id_card_number_masked || '身份证号已脱敏'}</code>
        <small><ShieldCheck size={12} />使用已认证的姓名开票</small>
      </div> : <div className="invoice-certification-gate" role="status"><CircleAlert size={15} /><span>{personalBlockedReason}</span></div>}
      <footer>
        <span>{blockedReason ? '当前开票条件尚未满足' : '提交时将再次核对本次金额；如额度变化，请重新选择'}</span>
        <button title={blockedReason || undefined} disabled={submitting || ordersLoading || preview || invoiceAmount === '0.00' || selectedRows.length === 0 || !currentInvoiceReady || !invoice.invoice_email.trim()}>{submitting ? '正在提交…' : `申请开票 ${moneyText(invoiceAmount)}`}</button>
      </footer>
    </form>

    <section className="corporate-finance-history">
      <h2>开票记录</h2>
      {invoices.length ? invoices.map(row => <article key={row.id}>
        <div className="invoice-history-main">
          <span className={`finance-status is-${statusTone(row.status)}`}>{row.status_label}</span>
          <strong>{row.invoice_title}</strong>
          <small>{basisLabel(row.invoice_basis)} · {moneyText(row.amount)} · {dateText(row.created_at)} · {row.orders?.length || 0} 笔明细</small>
        </div>
        <div className="invoice-history-actions">
          {row.review_remark && <small>{row.review_remark}</small>}
          {row.orders?.length > 0 && <button type="button" aria-expanded={expandedInvoice === row.id} onClick={() => setExpandedInvoice(current => current === row.id ? null : row.id)}>{expandedInvoice === row.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}开票明细</button>}
          {row.invoice_file_url && <a href={row.invoice_file_url} target="_blank" rel="noreferrer"><Download size={12} />下载电子发票</a>}
        </div>
        {expandedInvoice === row.id && <div className="invoice-history-orders">{row.orders.map(order => <p key={order.id}><span>{order.order_no} · {order.subject}</span><strong>{moneyText(order.amount)}</strong></p>)}</div>}
      </article>) : <div className="corporate-finance-empty">暂无开票记录</div>}
    </section>
    <footer className="corporate-finance-legal">开票请求超时或结果不明确时，系统会保留本次额度并进入人工核验，避免重复开票。</footer>
  </section>;
}
